import React from 'react';
import { Box, Container, Grid, Paper, Typography } from '@mui/material';
import { School, Search, Storefront } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  DEFAULT_PATH_CARDS,
  type PathCardContent,
} from '../../../shared/homepage/searchFirstDefaults';
import {
  HOMEPAGE_CARD_RADIUS,
  HOMEPAGE_CARD_SHADOW,
  HOMEPAGE_CARD_SHADOW_HOVER,
  HOMEPAGE_SECTION_PY,
} from '../../../shared/homepage/homepageLayout';
import { trackEvent } from '../../utils/analytics';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';

const iconFor = (key: PathCardContent['iconKey']) => {
  switch (key) {
    case 'host':
      return <Storefront sx={{ fontSize: 44 }} />;
    case 'instructor':
      return <School sx={{ fontSize: 44 }} />;
    case 'search':
      return <Search sx={{ fontSize: 44 }} />;
    default: {
      const _exhaustive: never = key;
      return _exhaustive;
    }
  }
};

export type PathCardsSectionProps = {
  title?: string;
  subtitle?: string;
  cards?: PathCardContent[] | null;
};

const PathCardsSection: React.FC<PathCardsSectionProps> = ({
  title = 'Platform Benefits',
  subtitle = 'Everything you need to find, host, and deliver training',
  cards,
}) => {
  const items = cards && cards.length === 3 ? cards : DEFAULT_PATH_CARDS;

  return (
    <Box
      sx={{
        py: HOMEPAGE_SECTION_PY.benefits,
        bgcolor: 'background.paper',
        px: AGENCY_SHELL_HORIZONTAL_PX,
      }}
    >
      <Container maxWidth="lg" disableGutters>
        <Typography
          variant="h2"
          component="h2"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.75rem', md: '2.5rem' },
            mb: 1,
            textAlign: 'center',
            letterSpacing: '-0.02em',
          }}
        >
          {title}
        </Typography>
        {subtitle ? (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 5, maxWidth: 560, mx: 'auto', lineHeight: 1.6 }}
          >
            {subtitle}
          </Typography>
        ) : null}
        <Grid container spacing={3}>
          {items.map((card) => (
            <Grid item xs={12} md={4} key={card.id}>
              <Paper
                component={RouterLink}
                to={card.href}
                elevation={0}
                onClick={() =>
                  trackEvent({
                    action: 'homepage_path_card_click',
                    category: 'engagement',
                    label: card.id,
                    custom_parameters: { href: card.href },
                  })
                }
                sx={{
                  display: 'block',
                  textDecoration: 'none',
                  color: 'inherit',
                  p: 4,
                  height: '100%',
                  borderRadius: HOMEPAGE_CARD_RADIUS,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: HOMEPAGE_CARD_SHADOW,
                  textAlign: 'center',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: HOMEPAGE_CARD_SHADOW_HOVER,
                    borderColor: 'primary.light',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{iconFor(card.iconKey)}</Box>
                <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  {card.label}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {card.sentence}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PathCardsSection;
